import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export interface Notification {
  id: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  project_id: string | null;
  type: string;
  title: string;
  message: string | null;
  priority: string;
  status: string;
  action_label: string | null;
  action_url: string | null;
  due_date: string | null;
  read_at?: string | null;
  created_at: string;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = ["notifications", user?.id] as const;

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notifications-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        qc.invalidateQueries({ queryKey: ["notifications"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, qc]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  const unreadCount = query.data?.filter((n) => n.status === "unread").length ?? 0;

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("notifications")
        .update({ status: "read", read_at: nowIso, updated_at: nowIso })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["notifications"] });
      const previous = qc.getQueryData<Notification[]>(queryKey);
      const nowIso = new Date().toISOString();
      if (previous) {
        qc.setQueryData<Notification[]>(
          queryKey,
          previous.map((n) =>
            n.id === id && n.status === "unread"
              ? { ...n, status: "read", read_at: nowIso }
              : n
          )
        );
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("notifications")
        .update({ status: "read", read_at: nowIso, updated_at: nowIso })
        .eq("status", "unread");
      if (error) throw error;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["notifications"] });
      const previous = qc.getQueryData<Notification[]>(queryKey);
      const nowIso = new Date().toISOString();
      if (previous) {
        qc.setQueryData<Notification[]>(
          queryKey,
          previous.map((n) =>
            n.status === "unread" ? { ...n, status: "read", read_at: nowIso } : n
          )
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ status: "dismissed", updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["notifications"] });
      const previous = qc.getQueryData<Notification[]>(queryKey);
      if (previous) {
        qc.setQueryData<Notification[]>(
          queryKey,
          previous.map((n) => (n.id === id ? { ...n, status: "dismissed" } : n))
        );
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return { ...query, unreadCount, markRead, markAllRead, dismiss };
};
